import { Alert, LinearProgress } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React, { Key, ReactElement, useLayoutEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { TreeStructure } from './TreeStructure';

export interface TreeWrapperProps<TNode> {
    loadRootNodes: (ids: Key[] | undefined) => Promise<TNode[]>;
    loadChildrenNodes: (parent: TNode) => Promise<TNode[]>;
    getNodeLabel: (node: TNode) => JSX.Element | string;
    getNodeId: (node: TNode) => Key;
    getParent: (node: TNode) => Key;
    isRootNode: (node: TNode) => boolean;
    isLeafNode: (node: TNode) => boolean;
    isSelectable?: (node: TNode) => boolean;
    selectedNodes: TNode[];
    onSelect?: (node: TNode[]) => void;
    multiSelect?: boolean;
    keys: string[];
    tooltipContent?: (node: TNode) => ReactElement | null;
    icon?: (node: TNode) => ReactElement | undefined;
    open?: boolean;
}

export interface TreeNode<T extends object> {
    key: Key;
    parentKey: Key;
    value: T;
    children: TreeNode<T>[];
}

export const TreeWrapper = <TNode extends object>(TreeProps: TreeWrapperProps<TNode>): ReactElement => {
    const { loadRootNodes, keys, selectedNodes, getNodeId, getParent, isRootNode, open } = TreeProps;
    const [treeData, setTreeData] = useState<TNode[]>([]);
    const [expanded, setExpanded] = useState<string[]>([]);

    const memoizedKeys = useMemo(() => {
        return ['load-root-nodes', ...keys];
    }, [open]);

    const {
        data: fetchedNodes,
        isLoading,
        isError: treeDataFailed,
    } = useQuery(memoizedKeys, () => loadRootNodes(selectedNodes?.map((node) => getNodeId(node))), {
        cacheTime: Infinity,
        staleTime: Infinity,
    });

    useLayoutEffect(() => {
        if (fetchedNodes) {
            const initiallyExpanded: string[] = [];

            const buildInitialTree: (nodes: TNode[], parentId: Key) => TreeNode<TNode>[] = (
                nodes: TNode[],
                parentId: Key
            ) => {
                return nodes
                    .filter((node) => getParent(node) === String(parentId))
                    .map((node) => {
                        const children = buildInitialTree(nodes, getNodeId(node));
                        if (children.length > 0) {
                            initiallyExpanded.push(getNodeId(node).toString());
                        }
                        return { value: node, children, parentKey: getParent(node), key: getNodeId(node) };
                    });
            };

            const uniqueParentNodes = [
                ...new Set(fetchedNodes.filter((node) => isRootNode(node)).map((node) => getParent(node))),
            ];
            const tree = uniqueParentNodes.flatMap(
                (parentNode) => buildInitialTree(fetchedNodes, parentNode),
                initiallyExpanded
            );
            const mapNodesRecursively: (nodes: TreeNode<TNode>[]) => TNode[] = (nodes: TreeNode<TNode>[]) => {
                return nodes.map((node) => ({
                    ...node.value,
                    children: node.children ? mapNodesRecursively(node.children) : [],
                }));
            };
            const treeObj = mapNodesRecursively(tree);

            setTreeData(treeObj);
            setExpanded(initiallyExpanded);
        }
    }, [fetchedNodes]);

    if (isLoading || treeData.length === 0) return <LinearProgress />;
    else if (treeDataFailed) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                <FormattedMessage id="common.error" />
            </Alert>
        );
    }

    return <TreeStructure {...TreeProps} treeData={treeData} expanded={expanded} setExpanded={setExpanded} />;
};
